// Silent refresh: when an authenticated HTMX request 401s because the
// access JWT has expired, attempt a single POST to /auth/refresh, then
// replay the original request. Two failures in a row (or a refresh-401)
// follow the server-supplied HX-Redirect.
//
// Server contract:
//   - The 401 carrying `HX-Trigger: auth-refresh-needed` *and* the
//     `X-Auth-Login-Url` header is the silent-refresh signal.
//   - The 401 from /auth/refresh itself carries `HX-Redirect` directly.
//
// Loaded as part of the page bundle; no inline expressions, no eval.
import { getCsrfToken } from "./csrf.js";

export const AUTH_REFRESHED_EVENT = "dagonet:auth-refreshed";

// Within-tab dedup: at most one in-flight POST /auth/refresh at a time.
let refreshInFlight = null;

// Cross-tab single-flight (#3424). Concurrent tabs must not each present
// the same *pre-rotation* refresh token: the second POST arrives after the
// family has rotated and trips reuse-detection, which revokes the family
// and hard-redirects the user to login (reason=session_revoked). We
// serialize refreshes across tabs with the Web Locks API, and skip a
// redundant POST when a sibling tab already rotated the (browser-shared)
// cookies within a short grace window. A BroadcastChannel + localStorage
// timestamp give the same coordination on browsers without Web Locks.
const REFRESH_LOCK = "dagonet-auth-refresh";
const REFRESH_TS_KEY = "dagonet:auth-refresh-ts";
const REFRESH_GRACE_MS = 5000;
let lastRefreshTs = 0;

let refreshChannel = null;
try {
  refreshChannel = "BroadcastChannel" in window ? new BroadcastChannel(REFRESH_LOCK) : null;
} catch (_e) {
  refreshChannel = null;
}
if (refreshChannel) {
  refreshChannel.onmessage = (evt) => {
    const d = evt.data || {};
    if (d.type === "refreshed" && typeof d.ts === "number" && d.ts > lastRefreshTs) {
      lastRefreshTs = d.ts;
    }
  };
}

function markRefreshed() {
  const ts = Date.now();
  if (ts > lastRefreshTs) lastRefreshTs = ts;
  try {
    localStorage.setItem(REFRESH_TS_KEY, String(ts));
  } catch (_e) {
    /* private mode */
  }
  if (refreshChannel) {
    try {
      refreshChannel.postMessage({ type: "refreshed", ts: ts });
    } catch (_e) {
      /* closed */
    }
  }
}

function recentlyRefreshed() {
  let newest = lastRefreshTs;
  try {
    const raw = localStorage.getItem(REFRESH_TS_KEY);
    if (raw) {
      const ts = parseInt(raw, 10);
      if (Number.isFinite(ts) && ts > newest) newest = ts;
    }
  } catch (_e) {
    /* localStorage unavailable — rely on in-memory + channel */
  }
  return newest > 0 && Date.now() - newest < REFRESH_GRACE_MS;
}

function refreshPost() {
  const token = getCsrfToken();
  return fetch("/auth/refresh", {
    method: "POST",
    credentials: "same-origin",
    headers: token ? { "X-CSRF-Token": token, "HX-Request": "true" } : { "HX-Request": "true" },
  });
}

function refreshCriticalSection() {
  // A sibling tab rotated the shared cookies moments ago → this tab is
  // already fresh, so skip the redundant POST (which would rotate again
  // for nothing). Synthesize a 204 so callers take their success path.
  if (recentlyRefreshed()) {
    return Promise.resolve(new Response(null, { status: 204 }));
  }
  return refreshPost().then((resp) => {
    // resp.ok already covers every 2xx (including the 204 rotation).
    if (resp.ok) markRefreshed();
    return resp;
  });
}

function singleFlightRefresh() {
  if (navigator.locks && typeof navigator.locks.request === "function") {
    return navigator.locks.request(REFRESH_LOCK, refreshCriticalSection);
  }
  // No Web Locks: the BroadcastChannel + localStorage grace check inside
  // refreshCriticalSection still collapses most concurrent bursts.
  return refreshCriticalSection();
}

export function refresh() {
  if (refreshInFlight) return refreshInFlight;
  refreshInFlight = singleFlightRefresh()
    .then((resp) => {
      refreshInFlight = null;
      return resp;
    })
    .catch((err) => {
      refreshInFlight = null;
      throw err;
    });
  return refreshInFlight;
}

export function navigateToLogin(url) {
  if (url) {
    window.location.assign(url);
  } else {
    window.location.assign("/auth/login");
  }
}

// Run a single (deduplicated) refresh, then either invoke the caller's
// success action (onOk) or follow the server-directed login URL when the
// refresh is declined. The "refresh → replay-on-success / redirect-on-
// failure" contract lives here so both the passive htmx:responseError
// listener and any direct caller (e.g. the notifications poller's circuit-
// breaker) share one code path. `refresh`'s in-flight guard collapses
// concurrent callers into one POST /auth/refresh.
export function ensureFresh(onOk, loginUrl) {
  return refresh()
    .then((resp) => {
      if (resp.ok) {
        // 2xx incl. the 204 rotation
        if (typeof onOk === "function") onOk(resp);
        return true;
      }
      // /auth/refresh declined — server tells us where to send the user.
      const hxRedirect = resp.headers.get("HX-Redirect");
      navigateToLogin(hxRedirect || loginUrl);
      return false;
    })
    .catch(() => {
      navigateToLogin(loginUrl);
      return false;
    });
}

function replayOriginal(evt) {
  const detail = evt.detail || {};
  const requestConfig = detail.requestConfig;
  const elt = detail.elt;
  if (requestConfig && elt) {
    const opts = {
      source: elt,
      target: requestConfig.target,
      swap: requestConfig.swap,
      values: requestConfig.parameters,
      headers: requestConfig.headers,
    };
    htmx.ajax(requestConfig.verb || "GET", requestConfig.path, opts);
    return;
  }
  // No usable HTMX replay metadata (element detached, or
  // a non-htmx caller). A full-page reload here would discard in-progress
  // work mid-task — the exact #3424 hard-redirect symptom — so we never
  // reload. Recover an idempotent GET by re-fetching quietly (the
  // originating poller re-renders on its next tick); anything else no-ops
  // and relies on the now-fresh session for the user's next real request.
  const verb = String(requestConfig?.verb || "").toUpperCase();
  const path = requestConfig?.path;
  if (verb === "GET" && path) {
    fetch(path, {
      credentials: "same-origin",
      headers: { "HX-Request": "true" },
    }).catch(() => {
      /* best-effort; poll retries anyway */
    });
  }
}

document.body.addEventListener("htmx:responseError", (evt) => {
  const xhr = evt.detail?.xhr;
  if (xhr?.status !== 401) return;
  const trigger = xhr.getResponseHeader("HX-Trigger") || "";
  if (trigger.indexOf("auth-refresh-needed") === -1) {
    // Server already decided redirect path; let default 401
    // handling (or another listener) take over.
    return;
  }
  evt.preventDefault();
  const loginUrl = xhr.getResponseHeader("X-Auth-Login-Url") || "/auth/login";
  ensureFresh(() => {
    replayOriginal(evt);
    // Announce success so a circuit-broken poller (one that stopped
    // itself on its own 401) can resume without waiting for a tick.
    document.body.dispatchEvent(new CustomEvent(AUTH_REFRESHED_EVENT));
  }, loginUrl);
});
